import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import { ApiError, createApi, type MediaInfo, type SessionStart, type WidgetConfig } from "./api";
import { AvatarSession } from "./avatar";
import { Composer } from "./components/Composer";
import { Controls } from "./components/Controls";
import { Header, type Size, type Status } from "./components/Header";
import { Stage } from "./components/Stage";
import { StatusCard } from "./components/StatusCard";
import { Transcript } from "./components/Transcript";
import { addLocalUserLine, upsertLine, type TranscriptLine } from "./transcript";
import { useFullscreenViewport } from "./useViewport";

type Phase =
  | { kind: "idle" }
  | { kind: "mic" }
  | { kind: "connecting" }
  | { kind: "live" }
  | { kind: "ended"; reason: string }
  | { kind: "error"; code: string };

const ERRORS: Record<string, { title: string; body: string }> = {
  mic_denied: { title: "Microphone needed", body: "Please allow microphone access in your browser, then try again." },
  forbidden: { title: "Assistant not available here", body: "This assistant isn't enabled for this website." },
  tenant_busy: { title: "Assistant is busy right now", body: "All lines are in use. Please try again in a moment." },
  quota_exceeded: { title: "Assistant is taking a break", body: "This assistant has reached its monthly limit. Please try again later." },
  vendor_unavailable: { title: "Temporarily unavailable", body: "The assistant couldn't be reached. Please try again shortly." },
  avatar_failed: { title: "Couldn't start the assistant", body: "The video connection failed. Please try again." },
  connection_lost: { title: "Connection lost", body: "The conversation was interrupted." },
};

export interface AppProps {
  apiBase: string;
  embedKey: string;
  config: WidgetConfig;
  /** Lets a host shell (popup launcher) reflect whether a conversation is running. */
  onLiveChange?: ((live: boolean) => void) | undefined;
  /** Present when rendered inside the popup panel: minimise callback + whether the panel is a full-screen sheet right now. */
  popup?: { onClose: () => void; open: boolean; fullscreen: boolean } | undefined;
}

export function App({ apiBase, embedKey, config, onLiveChange, popup }: AppProps) {
  const api = useRef(createApi(apiBase, embedKey)).current;
  const videoRef = useRef<HTMLVideoElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const session = useRef<SessionStart>();
  const avatar = useRef<AvatarSession>();
  const mic = useRef<MediaStream>();
  const timer = useRef<number>();
  const speechEndedAt = useRef<number>();
  /** What is currently on screen — the source of truth for get_screen_state and screen-update context. */
  const screen = useRef<{ media: MediaInfo; since: number }>();

  const [phase, setPhase] = useState<Phase>({ kind: "idle" });
  const [lines, setLines] = useState<TranscriptLine[]>([]);
  const [media, setMedia] = useState<MediaInfo>();
  const [muted, setMuted] = useState(false);
  const [status, setStatus] = useState<Status>("idle");
  const [size, setSize] = useState<Size>("normal");
  const [secondsLeft, setSecondsLeft] = useState<number>();
  const [needsTap, setNeedsTap] = useState(false);
  const tenantName = session.current?.tenantName ?? config.tenantName ?? "Assistant";
  const fullscreen = size === "full" || !!popup?.fullscreen;
  const fill = !!popup || size === "full";

  const teardown = useCallback(async (reason: "user" | "cap" | "client_error") => {
    window.clearInterval(timer.current);
    setSecondsLeft(undefined);
    setMedia(undefined);
    screen.current = undefined;
    setNeedsTap(false);
    setStatus("idle");
    window.clearTimeout(listenTimer.current);
    const s = session.current;
    const a = avatar.current;
    session.current = undefined;
    avatar.current = undefined;
    mic.current?.getTracks().forEach((t) => t.stop());
    mic.current = undefined;
    const anamId = a?.anamSessionId();
    if (a) await a.stop();
    if (s) await api.endSession(s, reason, anamId);
  }, [api]);

  const listenTimer = useRef<number>();
  const onUserSpeech = useCallback((state: "started" | "ended") => {
    window.clearTimeout(listenTimer.current);
    if (state === "started") {
      setStatus("listening");
      return;
    }
    speechEndedAt.current = performance.now();
    setStatus("thinking");
    // Anam's endpointing may not always be followed by a reply (noise); fall back to live after a while.
    listenTimer.current = window.setTimeout(() => setStatus((st) => (st === "thinking" ? "live" : st)), 6000);
  }, []);

  const onMessage = useCallback((id: string, role: "user" | "persona", text: string) => {
    setLines((prev) => upsertLine(prev, id, role, text));
  }, []);

  /** First persona text of a turn = reply started; measures user-turn-end → agent-reply latency. */
  const onAgentReply = useCallback(() => {
    window.clearTimeout(listenTimer.current);
    setStatus("live");
    if (speechEndedAt.current && session.current) {
      const ms = Math.round(performance.now() - speechEndedAt.current);
      speechEndedAt.current = undefined;
      void api.sendEvent(session.current, "latency.turn", { ms });
    }
  }, [api]);

  const screenState = () => {
    const cur = screen.current;
    return cur
      ? { displaying: cur.media.id, label: cur.media.label, kind: cur.media.kind, since_seconds: Math.round((Date.now() - cur.since) / 1000) }
      : { displaying: null, note: "Nothing is displayed; the avatar is in full view." };
  };

  const start = useCallback(async () => {
    setLines([]);
    setPhase({ kind: "mic" });
    try {
      mic.current = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setPhase({ kind: "error", code: "mic_denied" });
      return;
    }
    setPhase({ kind: "connecting" });
    let s: SessionStart;
    try {
      s = await api.startSession();
    } catch (err) {
      const code = err instanceof ApiError && err.code in ERRORS ? err.code : "vendor_unavailable";
      mic.current?.getTracks().forEach((t) => t.stop());
      setPhase({ kind: "error", code });
      return;
    }
    session.current = s;
    const a = new AvatarSession({
      onMessage,
      onUserSpeech,
      onAgentReply,
      onSessionReady: (id) => void api.sendEvent(s, "anam.session_ready", { anamSessionId: id }),
      onVideoPlaying: (played) => {
        setPhase({ kind: "live" });
        setStatus("live");
        setNeedsTap(!played);
        if (!played) void api.sendEvent(s, "video_autoplay_blocked");
        void api.sendEvent(s, "video_started");
        const endAt = Date.now() + s.maxSeconds * 1000;
        timer.current = window.setInterval(() => {
          const left = Math.max(0, Math.round((endAt - Date.now()) / 1000));
          setSecondsLeft(left);
          if (left === 0) {
            void teardown("cap").then(() => setPhase({ kind: "ended", reason: "cap" }));
          }
        }, 1000);
      },
      onClosed: (reason, details) => {
        if (!session.current) return; // we initiated the stop
        void api.sendEvent(s, "connection_closed", { reason, details: details ?? "" });
        void teardown("client_error").then(() => setPhase({ kind: "error", code: "connection_lost" }));
      },
      onWarning: (message) => void api.sendEvent(s, "server_warning", { message }),
      onShowMedia: async (mediaId) => {
        try {
          const info = await api.resolveMedia(s, mediaId);
          setMedia(info);
          screen.current = { media: info, since: Date.now() };
          return `Displayed "${info.label}" (${info.kind}) on the visitor's screen. It shows: ${info.card.summary} Key facts: ${info.card.facts.join("; ")}.`;
        } catch {
          return `Media "${mediaId}" is not available; nothing changed on screen. Only use ids from the media list.`;
        }
      },
      onHideMedia: async () => {
        const was = screen.current?.media.label;
        setMedia(undefined);
        screen.current = undefined;
        void api.sendEvent(s, "media.hidden");
        return was ? `Hidden "${was}". The avatar is back to full view; nothing is displayed.` : "Nothing was displayed; the avatar is in full view.";
      },
      onGetScreenState: async () => JSON.stringify(screenState()),
      onDescribeMedia: async (mediaId) => {
        try {
          const d = await api.describeMedia(s, mediaId);
          return `${d.label} (${d.kind}): ${d.card.summary} Facts: ${d.card.facts.join("; ")}.${d.card.text ? ` Visible text: ${d.card.text}.` : ""} Show when: ${d.card.whenToShow}`;
        } catch {
          return `No media with id "${mediaId}" is available.`;
        }
      },
    });
    avatar.current = a;
    try {
      await a.start(s, videoRef.current!, mic.current!); // Anam uses our mic stream; teardown stops it
    } catch (err) {
      void api.sendEvent(s, "avatar_failed", { error: err instanceof Error ? err.message : String(err) });
      await teardown("client_error");
      setPhase({ kind: "error", code: "avatar_failed" });
    }
  }, [api, onMessage, onUserSpeech, onAgentReply, teardown]);

  const end = useCallback(async () => {
    await teardown("user");
    setPhase({ kind: "ended", reason: "user" });
  }, [teardown]);

  const toggleMute = useCallback(() => {
    const next = !muted;
    avatar.current?.setMuted(next);
    setMuted(next);
  }, [muted]);

  useEffect(() => {
    const onHide = () => {
      if (session.current) void teardown("user");
    };
    window.addEventListener("pagehide", onHide);
    return () => {
      window.removeEventListener("pagehide", onHide);
      void teardown("user");
    };
  }, [teardown]);

  const sendText = useCallback((text: string) => {
    if (!avatar.current || !session.current) return;
    setLines((prev) => addLocalUserLine(prev, text));
    speechEndedAt.current = performance.now();
    setStatus("thinking");
    avatar.current.sendText(text);
    void api.sendEvent(session.current, "text_message", { chars: text.length });
  }, [api]);

  const closeMedia = useCallback(() => {
    const was = screen.current?.media.label;
    setMedia(undefined);
    screen.current = undefined;
    if (session.current) void api.sendEvent(session.current, "media.hidden", { by: "user" });
    if (was) avatar.current?.sendContext(`Screen update: the visitor closed "${was}" themselves. Nothing is displayed now; the avatar is in full view. Do not assume it is still visible.`);
  }, [api]);

  const onEscape = useCallback(() => {
    if (size === "full") setSize("normal");
    else popup?.onClose();
  }, [size, popup]);
  useFullscreenViewport(fullscreen, rootRef.current, onEscape);
  /** Entering full mode from an inline embed: keep the placeholder height so the host page doesn't jump. */
  const setSizeMeasured = useCallback((next: Size) => {
    const el = rootRef.current;
    if (el) el.style.setProperty("--wk-placeholder", next === "full" ? `${el.offsetHeight}px` : "0px");
    setSize(next);
  }, []);
  useEffect(() => {
    if (popup?.open) cardRef.current?.focus({ preventScroll: true });
  }, [popup?.open]);
  const tapToPlay = useCallback(() => {
    void avatar.current?.resumeVideo().then((ok) => setNeedsTap(!ok));
  }, []);

  const live = phase.kind === "live";
  useEffect(() => onLiveChange?.(live), [live, onLiveChange]);
  return (
    <div class={`wk-root ${size}`} ref={rootRef}>
    <div ref={cardRef} tabIndex={-1} class={`wk wk-${size}${fill ? " wk-fill" : ""}`} role={fullscreen ? "dialog" : undefined} aria-modal={fullscreen ? "true" : undefined} aria-label={`${tenantName} assistant`}>
      <Header name={tenantName} status={live ? status : "idle"} secondsLeft={live ? secondsLeft : undefined} size={size} canWiden={!popup} onClose={popup?.fullscreen ? popup.onClose : undefined} onSize={setSizeMeasured} />
      <div class="wk-body">
      <Stage videoRef={videoRef} media={live ? media : undefined} listening={live && status === "listening"} needsTap={live && needsTap} onTap={tapToPlay} onCloseMedia={closeMedia}>
        {phase.kind === "idle" && <StatusCard title="Talk to our assistant" body="A live video conversation — ask anything, or ask to see the pricing chart." action={{ label: "Tap to talk", onClick: () => void start() }} fine="Uses your microphone. Nothing is recorded by this site." />}
        {phase.kind === "mic" && <StatusCard title="Allow microphone" body="Please allow microphone access in your browser." spinner />}
        {phase.kind === "connecting" && <StatusCard title="Connecting…" body="Waking up your assistant." spinner />}
        {phase.kind === "ended" && <StatusCard title={phase.reason === "cap" ? "Time's up for this session" : "Thanks for chatting!"} body="You can start a new conversation any time." action={{ label: "Start again", onClick: () => void start(), ghost: true }} />}
        {phase.kind === "error" && <StatusCard title={ERRORS[phase.code]?.title ?? "Something went wrong"} body={ERRORS[phase.code]?.body} action={{ label: "Try again", onClick: () => void start(), ghost: true }} />}
      </Stage>
      {/* Before the call the card is just header + stage (+ tiny footer); the conversation UI appears once live. */}
      {live && (
        <div class="wk-side">
          <Transcript lines={lines} name={tenantName} thinking={status === "thinking"} />
          <Composer disabled={false} onSend={sendText} />
          <Controls live muted={muted} poweredBy={config.appearance?.poweredBy !== false} onToggleMute={toggleMute} onEnd={() => void end()} />
        </div>
      )}
      </div>
      {!live && config.appearance?.poweredBy !== false && <div class="wk-controls idle"><span class="wk-foot"><a href="https://workky.cloud" target="_blank" rel="noopener">Powered by Workky</a></span></div>}
    </div>
    </div>
  );
}
